import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Plus, Pencil, Trash2, Image, Loader2, GripVertical, ExternalLink } from 'lucide-react'
import type {
  LogoRule,
  CreateLogoRuleRequest,
  UpdateLogoRuleRequest,
  ComplianceSeverity,
} from '@/lib/api/types'
import { cn } from '@/lib/utils'

interface LogoRulesPanelProps {
  logoRules: LogoRule[]
  loading: boolean
  onAdd: (data: CreateLogoRuleRequest) => Promise<void>
  onUpdate: (ruleId: string, data: UpdateLogoRuleRequest) => Promise<void>
  onDelete: (ruleId: string) => Promise<void>
}

const ALLOWED_PLACEMENTS = [
  { value: 'header', label: 'Header' },
  { value: 'footer', label: 'Footer' },
  { value: 'cover', label: 'Cover Page' },
  { value: 'sidebar', label: 'Sidebar' },
  { value: 'watermark', label: 'Watermark' },
  { value: 'inline', label: 'Inline' },
]

const defaultFormData: CreateLogoRuleRequest = {
  name: '',
  assetId: '',
  minWidth: undefined,
  maxWidth: undefined,
  minHeight: undefined,
  maxHeight: undefined,
  clearSpacePercent: 10,
  allowedPlacements: [],
  severity: 'Warning',
}

export function LogoRulesPanel({
  logoRules,
  loading,
  onAdd,
  onUpdate,
  onDelete,
}: LogoRulesPanelProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<LogoRule | null>(null)
  const [deleteRule, setDeleteRule] = useState<LogoRule | null>(null)
  const [formData, setFormData] = useState<CreateLogoRuleRequest>(defaultFormData)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleOpenDialog = (rule?: LogoRule) => {
    if (rule) {
      setEditingRule(rule)
      setFormData({
        name: rule.name,
        assetId: rule.assetId,
        minWidth: rule.minWidth,
        maxWidth: rule.maxWidth,
        minHeight: rule.minHeight,
        maxHeight: rule.maxHeight,
        clearSpacePercent: rule.clearSpacePercent,
        allowedPlacements: rule.allowedPlacements,
        severity: rule.severity,
      })
    } else {
      setEditingRule(null)
      setFormData(defaultFormData)
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingRule(null)
    setFormData(defaultFormData)
  }

  const handleSave = async () => {
    if (!formData.name || !formData.assetId) return

    setSaving(true)
    try {
      if (editingRule) {
        await onUpdate(editingRule.id, formData as UpdateLogoRuleRequest)
      } else {
        await onAdd(formData)
      }
      handleCloseDialog()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteRule) return

    setDeleting(true)
    try {
      await onDelete(deleteRule.id)
      setDeleteRule(null)
    } finally {
      setDeleting(false)
    }
  }

  const togglePlacement = (placement: string) => {
    const current = formData.allowedPlacements || []
    const updated = current.includes(placement)
      ? current.filter((p) => p !== placement)
      : [...current, placement]
    setFormData({ ...formData, allowedPlacements: updated })
  }

  const getSeverityBadge = (severity: ComplianceSeverity) => {
    const styles = {
      Error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      Warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
      Info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    }
    return <Badge className={cn('text-xs', styles[severity])}>{severity}</Badge>
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading logo rules...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Logo Rules
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            Define logo usage, placement, and size constraints
          </p>
        </div>
        <Button size="sm" onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-1" />
          Add Logo
        </Button>
      </div>

      {logoRules.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
          <Image className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No logo rules defined</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
            Add logo rules to enforce brand asset usage
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {logoRules
            .sort((a, b) => a.order - b.order)
            .map((rule) => (
              <div
                key={rule.id}
                className="group bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden"
              >
                <div className="flex items-start gap-3 p-3">
                  <GripVertical className="h-4 w-4 text-neutral-400 shrink-0 mt-1 cursor-grab" />
                  <div className="w-16 h-16 rounded-md border border-neutral-200 dark:border-neutral-600 overflow-hidden bg-neutral-50 dark:bg-neutral-700 shrink-0">
                    {rule.thumbnailUrl || rule.assetUrl ? (
                      <img
                        src={rule.thumbnailUrl || rule.assetUrl}
                        alt={rule.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="h-6 w-6 text-neutral-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100 truncate">
                        {rule.name}
                      </span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {rule.assetUrl && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => window.open(rule.assetUrl, '_blank')}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleOpenDialog(rule)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setDeleteRule(rule)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/30"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {getSeverityBadge(rule.severity)}
                      {(rule.minWidth || rule.maxWidth || rule.minHeight || rule.maxHeight) && (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                          Size: {rule.minWidth || 0}-{rule.maxWidth || 'any'}px x {rule.minHeight || 0}-{rule.maxHeight || 'any'}px
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                      <span>Clear space: {rule.clearSpacePercent}%</span>
                    </div>
                    {rule.allowedPlacements.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {rule.allowedPlacements.map((placement) => (
                          <span
                            key={placement}
                            className="text-xs px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-neutral-600 dark:text-neutral-300"
                          >
                            {ALLOWED_PLACEMENTS.find((p) => p.value === placement)?.label || placement}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Logo Rule' : 'Add Logo Rule'}</DialogTitle>
            <DialogDescription>
              Configure logo name, asset, and placement constraints.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Logo Name</Label>
              <Input
                placeholder="e.g., Primary Logo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Asset ID</Label>
              <Input
                placeholder="Enter asset ID from DAM"
                value={formData.assetId}
                onChange={(e) => setFormData({ ...formData, assetId: e.target.value })}
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Reference the logo asset from the Digital Asset Manager
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Width (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 100"
                  value={formData.minWidth || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minWidth: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Max Width (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 400"
                  value={formData.maxWidth || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxWidth: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Height (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 50"
                  value={formData.minHeight || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minHeight: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Max Height (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 200"
                  value={formData.maxHeight || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxHeight: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Clear Space (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.clearSpacePercent}
                onChange={(e) =>
                  setFormData({ ...formData, clearSpacePercent: parseInt(e.target.value) || 0 })
                }
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Minimum clear space around the logo as a percentage of logo size
              </p>
            </div>

            <div className="space-y-2">
              <Label>Allowed Placements</Label>
              <div className="flex flex-wrap gap-2">
                {ALLOWED_PLACEMENTS.map((placement) => (
                  <button
                    key={placement.value}
                    type="button"
                    onClick={() => togglePlacement(placement.value)}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-md border transition-colors',
                      formData.allowedPlacements?.includes(placement.value)
                        ? 'bg-neutral-900 text-white border-neutral-900 dark:bg-neutral-100 dark:text-neutral-900 dark:border-neutral-100'
                        : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-400 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-600'
                    )}
                  >
                    {placement.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={formData.severity}
                onValueChange={(value) =>
                  setFormData({ ...formData, severity: value as ComplianceSeverity })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Error">Error - Block non-compliant</SelectItem>
                  <SelectItem value="Warning">Warning - Show warning</SelectItem>
                  <SelectItem value="Info">Info - Informational only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !formData.name || !formData.assetId}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRule ? 'Save Changes' : 'Add Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteRule} onOpenChange={(open) => !open && setDeleteRule(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Logo Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the logo rule for "{deleteRule?.name}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRule(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
